// TaskFlow · editor de notas colaborativo e historial de versiones.
// Autoguardado continuo (documento en vivo + foto HTML), versión automática de protección cada 10 minutos
// de edición y versión final con el botón «Guardar». Las versiones se conservan hasta que alguien las borre.
import { api, estado } from './api.js';
import { e, icono, haceTiempo, fechaLarga, hora12, aviso, anunciar, confirmar } from './util.js';
import { crearEditor } from './editor.js';
import { pintarPresencia } from './detalle.js';

const DIEZ_MINUTOS = 10 * 60 * 1000;

export const Nota = {
  titulo: 'Nota',
  async render(main, ctx, [uid]) {
    main.innerHTML = '<div class="nota"><div class="esqueleto"></div><div class="esqueleto" style="height:50vh"></div></div>';
    const { nota } = await api.get('/api/notas/' + uid);
    ctx.titulo(nota.titulo || 'Nota');
    const ed = nota.puede_editar;
    main.innerHTML = `<article class="nota" aria-labelledby="nt-titulo">
      <nav class="meta" aria-label="Ruta"><a href="/pila/${e(nota.pila.uid)}" data-ruta>${e(nota.pila.nombre)}</a> › Nota</nav>
      <label class="solo-lector" for="nt-titulo">Título de la nota</label>
      <textarea id="nt-titulo" class="titulo-editable" rows="1" maxlength="200" placeholder="Sin título" ${ed ? '' : 'readonly'}>${e(nota.titulo)}</textarea>
      <div class="barra-nota">
        <span class="tf-avatares" id="nt-presencia"></span>
        <span class="estado-guardado" id="nt-estado" role="status">${ed ? 'Autoguardado activo' : 'Solo lectura'}</span>
        <a class="tf-boton" href="/nota/${e(nota.uid)}/versiones" data-ruta>${icono('historial')}Versiones (${nota.versiones})</a>
        ${ed ? `<button class="tf-boton tf-boton--principal" type="button" id="nt-guardar" aria-describedby="nt-ayuda">${icono('guardar')}Guardar</button>` : ''}
        ${ed ? `<button class="tf-boton tf-boton--peligro" type="button" id="nt-eliminar">${icono('papelera')}Eliminar nota</button>` : ''}
      </div>
      ${ed ? '<p class="meta solo-lector" id="nt-ayuda">Guardar crea una versión final que podrás restaurar. Atajo: Ctrl+S.</p>' : ''}
      <div id="nt-editor"></div>
      <p class="meta">${ed ? 'Los cambios se guardan solos, también sin conexión. Cada 10 minutos de edición se crea una versión de protección; «Guardar» crea una versión final.' : 'Tienes acceso de lector a esta pila: puedes leer, no editar.'} · Atajo de la barra de formato: Alt+F10.</p>
    </article>`;
    const $n = (s) => main.querySelector(s);
    const estadoTxt = (t) => { const s = $n('#nt-estado'); if (s) s.innerHTML = t; };
    const titulo = $n('#nt-titulo');
    const ajustar = () => { titulo.style.height = 'auto'; titulo.style.height = titulo.scrollHeight + 'px'; };
    ajustar(); titulo.addEventListener('input', ajustar);
    let ultimaFoto = nota.html || '';
    let ultimaVersion = Date.now();
    let cambiosDesdeVersion = false;
    let vivo = 'connecting';

    const foto = async (html) => {
      if (!ed || html === ultimaFoto) return;
      ultimaFoto = html;
      cambiosDesdeVersion = true;
      if (!estado.enLinea) { estadoTxt(`${icono('sinred').replace('class="icono"', 'class="icono" style="width:14px;height:14px"')} Guardado en este dispositivo`); return; }
      try { await api.post(`/api/notas/${nota.uid}/foto`, { html }); estadoTxt(`${icono('ok').replace('class="icono"', 'class="icono" style="width:14px;height:14px"')} Guardado ${new Intl.DateTimeFormat('es-CO', { hour: 'numeric', minute: '2-digit', timeZone: 'America/Bogota' }).format(new Date())}`); }
      catch { estadoTxt('Guardado en este dispositivo · se enviará al reconectar'); }
    };
    const editor = crearEditor({
      contenedor: $n('#nt-editor'), documento: nota.documento, htmlInicial: nota.html, usuario: estado.usuario,
      etiqueta: 'Contenido de la nota', marcador: 'Escribe la nota…',
      alCambiar: (html) => foto(html),
      alPresencia: (p) => pintarPresencia($n('#nt-presencia'), p),
      alSincronizado: () => {
        // Restauración de versión: se aplica al documento en vivo una vez sincronizado con el servidor.
        let r = null; try { r = JSON.parse(sessionStorage.getItem('tf-restaurar-' + uid) || 'null'); sessionStorage.removeItem('tf-restaurar-' + uid); } catch { /* */ }
        if (r && ed) { editor.reemplazar(r.html); ultimaFoto = r.html; anunciar('Versión restaurada en la nota'); }
      },
      alEstado: (s) => { vivo = s; if (s === 'disconnected' && ed) estadoTxt('Sin conexión en vivo · tus cambios se guardan en este dispositivo'); if (s === 'connected' && ed) estadoTxt('Conectado · autoguardado activo'); },
    });
    ctx.alSalir(() => {
      clearInterval(protector);
      if (ed && editor.html() !== ultimaFoto && estado.enLinea) api.post(`/api/notas/${nota.uid}/foto`, { html: editor.html() }).catch(() => {});
      editor.destruir();
    });

    // Versión automática de protección cada 10 minutos de edición (la crea una sola persona: la de menor id de sesión).
    const protector = setInterval(async () => {
      if (!ed || !cambiosDesdeVersion || Date.now() - ultimaVersion < DIEZ_MINUTOS || !estado.enLinea || !editor.soyResponsable()) return;
      try { await api.post(`/api/notas/${nota.uid}/version`, { tipo: 'auto', html: editor.html() }); ultimaVersion = Date.now(); cambiosDesdeVersion = false; } catch { /* se reintenta */ }
    }, 30000);

    if (ed) {
      titulo.addEventListener('change', async () => {
        try { await api.enviar(`/api/notas/${nota.uid}/titulo`, { titulo: titulo.value.trim() }, 'Título de nota'); ctx.titulo(titulo.value.trim() || 'Nota'); } catch (err) { aviso(err.message); }
      });
      titulo.addEventListener('keydown', (ev) => { if (ev.key === 'Enter') { ev.preventDefault(); editor.editor.commands.focus('start'); } });
      const guardar = async () => {
        if (!estado.enLinea) { aviso('Sin conexión: la nota está guardada en este dispositivo. La versión final se crea al reconectar.'); return; }
        try {
          if (titulo.value.trim() !== nota.titulo) await api.post(`/api/notas/${nota.uid}/titulo`, { titulo: titulo.value.trim() });
          await api.post(`/api/notas/${nota.uid}/version`, { tipo: 'final', html: editor.html() });
          ultimaVersion = Date.now(); cambiosDesdeVersion = false; ultimaFoto = editor.html();
          aviso('Versión guardada'); estadoTxt(`${icono('ok').replace('class="icono"', 'class="icono" style="width:14px;height:14px"')} Versión final guardada`);
        } catch (err) { aviso(err.message); }
      };
      $n('#nt-guardar').addEventListener('click', guardar);
      // Eliminar: la nota va a la papelera (90 días). Pueden hacerlo los editores de la pila y el administrador,
      // que tiene acceso de propietario en todas las pilas.
      $n('#nt-eliminar').addEventListener('click', async () => {
        if (!await confirmar({ titulo: `¿Eliminar «${titulo.value.trim() || 'Sin título'}»?`, texto: 'La nota y sus versiones pasan a la papelera. Puedes restaurarla durante 90 días.' })) return;
        try {
          await api.post(`/api/notas/${nota.uid}/eliminar`);
          ctx.ir('/pila/' + nota.pila.uid);
          aviso('Nota movida a la papelera', { deshacer: async () => { await api.post('/api/papelera/restaurar', { tipo: 'nota', uid: nota.uid }); ctx.ir('/nota/' + nota.uid); } });
        } catch (err) { aviso(err.message); }
      });
      main.addEventListener('keydown', (ev) => { if ((ev.ctrlKey || ev.metaKey) && ev.key.toLowerCase() === 's') { ev.preventDefault(); guardar(); } });
    }
    // Restauración hecha por otra persona: ya llega por el documento en vivo; solo se anuncia.
    ctx.alEventoVivo((ev) => { if (ev.recurso === 'nota' && ev.uid === nota.uid && ev.restaurada && ev.por !== estado.usuario.id) aviso(ev.texto); });
    if (!nota.titulo && ed) titulo.focus(); else editor.editor.commands.focus('end');
  },
};

export const Versiones = {
  titulo: 'Historial de versiones',
  async render(main, ctx, [uid]) {
    main.innerHTML = '<div class="vista"><div class="esqueleto"></div></div>';
    const [{ nota }, { versiones, puede_editar: ed }] = await Promise.all([api.get('/api/notas/' + uid), api.get(`/api/notas/${uid}/versiones`)]);
    ctx.titulo('Versiones · ' + (nota.titulo || 'Sin título'));
    main.innerHTML = `<div class="vista">
      <div class="bloque" style="gap:4px"><h1 class="titulo-vista" id="titulo-vista" tabindex="-1">Historial de versiones</h1>
        <p class="subtitulo"><a href="/nota/${e(uid)}" data-ruta>${e(nota.titulo || 'Sin título')}</a> · <span class="marca-version final">Final</span> creada con «Guardar» · <span class="marca-version auto">Automática</span> protección cada 10 minutos</p></div>
      ${versiones.length ? `<div class="versiones">
        <ul class="lista-versiones" aria-label="Versiones">${versiones.map((v, i) => `<li><button type="button" data-id="${v.id}" ${i === 0 ? 'aria-current="true"' : ''}>
          <span class="marca-version ${v.tipo}">${v.tipo === 'final' ? 'Final' : 'Automática'}</span>
          <span><b>${e(fechaLarga(v.creado_en.slice(0, 10)))}, ${e(hora12(v.creado_en.slice(11, 16)))}</b><br><span class="meta">${e(v.autor)} · ${e(haceTiempo(v.creado_en))}</span></span></button></li>`).join('')}</ul>
        <section aria-labelledby="vp-t" class="tf-pila" style="gap:12px">
          <div style="display:flex;gap:8px;align-items:center;flex-wrap:wrap"><h2 id="vp-t" style="font:700 18px/24px var(--font-ui);flex:1">Vista previa</h2>
          ${ed ? `<button class="tf-boton" type="button" id="vp-borrar">${icono('papelera')}Borrar versión</button><button class="tf-boton tf-boton--principal" type="button" id="vp-restaurar">${icono('reabrir')}Restaurar</button>` : ''}</div>
          <div class="vista-previa" id="vp" tabindex="0" aria-label="Contenido de la versión seleccionada"></div></section></div>`
      : '<div class="vacio"><strong>Todavía no hay versiones.</strong>Usa «Guardar» en la nota para crear la primera versión final.</div>'}</div>`;
    if (!versiones.length) return;
    let actual = versiones[0].id;
    const ver = async (id) => {
      actual = id;
      main.querySelectorAll('.lista-versiones button').forEach((b) => b.setAttribute('aria-current', String(Number(b.dataset.id) === id)));
      const { version } = await api.get('/api/versiones/' + id);
      main.querySelector('#vp').innerHTML = `<h2 style="font:800 24px/30px var(--font-ui);margin-bottom:12px">${e(version.titulo || 'Sin título')}</h2><div class="ProseMirror" style="padding:0;min-height:0">${version.html}</div>`;
    };
    main.querySelector('.lista-versiones').addEventListener('click', (ev) => { const b = ev.target.closest('button'); if (b) ver(Number(b.dataset.id)); });
    main.querySelector('#vp-restaurar')?.addEventListener('click', async () => {
      if (!await confirmar({ titulo: '¿Restaurar esta versión?', texto: 'La nota volverá a este contenido para todos. Antes se guarda el contenido actual como versión automática, así que puedes deshacerlo.', boton: 'Restaurar', peligro: false })) return;
      try {
        const r = await api.post(`/api/versiones/${actual}/restaurar`);
        // Se aplica al documento en vivo para que lo vean al instante quienes editan.
        sessionStorage.setItem('tf-restaurar-' + uid, JSON.stringify({ html: r.html }));
        aviso('Versión restaurada');
        ctx.ir('/nota/' + uid);
      } catch (err) { aviso(err.message); }
    });
    main.querySelector('#vp-borrar')?.addEventListener('click', async () => {
      if (!await confirmar({ titulo: '¿Borrar esta versión?', texto: 'Se elimina para siempre del historial. El contenido actual de la nota no cambia.' })) return;
      try { await api.post(`/api/versiones/${actual}/eliminar`); aviso('Versión borrada'); ctx.recargar(); } catch (err) { aviso(err.message); }
    });
    ver(actual);
  },
};
